import { ScaleElement } from '@scale/base-element';
import { VariantSelectedEvent } from '@scale/events';
import { rollText } from '@scale/utilities';

class VariantPicker extends ScaleElement {
  #variants = [];

  setup() {
    const json = this.querySelector('[data-ref="variants-json"]');
    if (json) this.#variants = JSON.parse(json.textContent);

    this.#setupMediaPreview();
  }

  handleOptionChange() {
    const selectedOptions = this.#getSelectedOptions();
    const variant = this.#findVariant(selectedOptions);

    if (!variant) return;

    this.#updateURL(variant);
    this.#updateFormInput(variant);
    this.#updateAvailability(selectedOptions);
    this.#updateSelectedLabels(selectedOptions);
    this.#handleCombinedListing(selectedOptions);

    this.broadcast(new VariantSelectedEvent(variant, this.dataset.productId));
  }

  #getSelectedOptions() {
    const options = [];
    for (const fieldset of this.querySelectorAll('fieldset[data-option-index]')) {
      const checked = fieldset.querySelector('input:checked') || fieldset.querySelector('select');
      if (checked) options[Number(fieldset.dataset.optionIndex)] = checked.value;
    }
    return options;
  }

  #matchesOptions(variant, selectedOptions) {
    return variant.options.every((opt, i) => selectedOptions[i] === undefined || opt === selectedOptions[i]);
  }

  #findVariant(selectedOptions) {
    return this.#variants.find((v) => this.#matchesOptions(v, selectedOptions));
  }

  #updateURL(variant) {
    if (this.hasAttribute('data-skip-url-update')) return;
    const url = new URL(window.location);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url);
  }

  #updateFormInput(variant) {
    const form = this.closest('form') || document.querySelector(`form[data-type="add-to-cart-form"]`);
    const input = form?.querySelector('input[name="id"]');
    if (input) {
      input.value = variant.id;
      input.disabled = !variant.available;
    }
  }

  #updateSelectedLabels(selectedOptions) {
    selectedOptions.forEach((value, i) => {
      const label = this.ref(`selected-value-${i}`);
      if (!label) return;
      const shown = (label.lastElementChild ?? label).textContent.trim();
      if (shown !== value) {
        const values = [...this.querySelectorAll(`fieldset[data-option-index="${i}"] :is(input[type="radio"], option)`)].map((input) => input.value);
        rollText(label, value, values.indexOf(value) < values.indexOf(shown) ? -1 : 1);
      }

      const tag = this.ref(`sold-out-${i}`);
      const soldOut = !this.#isAvailable(selectedOptions, i, value);
      if (tag.hidden === soldOut) {
        tag.hidden = !soldOut;
        if (soldOut && matchMedia('(prefers-reduced-motion: no-preference)').matches) {
          const motion = getComputedStyle(tag);
          tag.animate([{ opacity: 0, translate: `${motion.direction === 'rtl' ? 4 : -4}px 0` }, { opacity: 1, translate: '0 0' }], {
            duration: parseFloat(motion.getPropertyValue('--duration-normal')),
            easing: motion.getPropertyValue('--ease-spring'),
          });
        }
      }
    });
  }

  #isAvailable(selectedOptions, index, value) {
    const testOptions = [...selectedOptions];
    testOptions[index] = value;
    return this.#variants.some((v) => v.available && this.#matchesOptions(v, testOptions));
  }

  #updateAvailability(selectedOptions) {
    for (const fieldset of this.querySelectorAll('fieldset[data-option-index]')) {
      const index = Number(fieldset.dataset.optionIndex);
      for (const input of fieldset.querySelectorAll('input[type="radio"]')) {
        const match = this.#isAvailable(selectedOptions, index, input.value);
        const label = input.closest('label');
        if (label) {
          label.classList.toggle('product-variant-picker__button--unavailable', !match);
          label.classList.toggle('product-variant-picker__swatch--unavailable', !match);
          label.classList.toggle('product-variant-picker__card--unavailable', !match);
        }
      }
    }
  }


  #setupMediaPreview() {
    for (const label of this.querySelectorAll('.product-variant-picker__button, .product-variant-picker__swatch, .product-variant-picker__card')) {
      const input = label.querySelector('input[type="radio"]');
      if (!input) continue;

      this.on('mouseenter', () => {
        this.debounce('media-preview', () => {
          const variant = this.#findVariantForPreview(input);
          if (variant?.featured_media?.id) {
            this.emit('scale:media:preview', { mediaId: variant.featured_media.id });
          }
        }, 80);
      }, { target: label });
    }
  }

  #findVariantForPreview(input) {
    const fieldset = input.closest('fieldset');
    const index = Number(fieldset.dataset.optionIndex);
    const selectedOptions = [...this.#getSelectedOptions()];
    selectedOptions[index] = input.value;
    return this.#findVariant(selectedOptions);
  }

  #handleCombinedListing(selectedOptions) {
    for (const fieldset of this.querySelectorAll('fieldset[data-option-index]')) {
      const index = Number(fieldset.dataset.optionIndex);
      const value = selectedOptions[index];
      const link = fieldset.querySelector(`[data-combined-listing="${CSS.escape(value)}"]`);
      if (link) window.location.href = link.href;
    }
  }
}

if (!customElements.get('product-variant-picker')) {
  customElements.define('product-variant-picker', VariantPicker);
}
